import { useState } from 'react';
import { useViewMode } from '@/shared/hooks/useViewMode';
import { Chip, StatusDot } from '@/shared/ui/atoms';
import { ViewModeToggle, type ViewModeOption } from '@/shared/ui/ViewModeToggle';
import { SortPicker } from '@/features/project-board/components/SortPicker';
import { DateSort, type SortKey } from '@/features/project-board/sorting';
import { useBoard } from '@/features/project-board/BoardProvider';
import type { DrawerChild, DrawerPayload } from '@/features/project-board/components/DetailDrawer';
import { DependencyGraph, DependencyLegend } from '@/features/project-board/components/DependencyGraph';
import { DrawerPayloads } from '@/features/project-board/drawer-payloads';
import { BUCKETS, type BoardIndex, type Task } from '@/features/project-board/types';
import { ViewFrame } from '@/features/project-board/views/ViewFrame';
import { EpicMembership } from '@/features/project-board/identity';
import { OwnerRoster } from '@/features/project-board/owners';

const HEAD =
  '[&>th]:sticky [&>th]:top-topbar [&>th]:z-10 [&>th]:bg-[#f0f3f6] [&>th]:px-2.5 [&>th]:py-2.5 [&>th]:text-left [&>th]:text-xs [&>th]:font-semibold [&>th]:text-muted [&>th]:shadow-[inset_0_-1px_0_var(--color-line)]';

const EDGE: Record<string, string> = {
  todo: 'border-l-todo',
  progress: 'border-l-progress',
  done: 'border-l-done',
  dropped: 'border-l-dropped',
  unknown: 'border-l-unknown',
};

type BacklogViewMode = 'table' | 'card' | 'graph';

const VIEW_MODES: ReadonlyArray<ViewModeOption<BacklogViewMode>> = [
  { key: 'table', label: '테이블', glyph: '☰' },
  { key: 'card', label: '카드', glyph: '▤' },
  { key: 'graph', label: '그래프', glyph: '⇄' },
];

/** task에는 등록 시점이 없다 — `updatedAt`뿐이라 "갱신" 기준으로만 낼 수 있다(`sorting.ts` 표 참고). */
const SORT_OPTIONS = DateSort.options('갱신');

export function BacklogView(): React.JSX.Element {
  const { index, tasks } = useBoard();
  const [mode, setMode] = useViewMode<BacklogViewMode>('project-board.backlog.view', 'graph');
  // 백로그는 "남은 일" 목록이라 완료를 기본으로 뺀다. 공용 필터가 아니라 이 화면 상태인 이유는
  // 같은 플래그를 보드(칸반)가 쓰면 완료 레인이 통째로 비기 때문이다.
  const [hideDone, setHideDone] = useState(true);
  // task에는 등록 시점이 없어(sorting.ts 표) 기본은 갱신 최신순 — 등록일 기본 정렬 요청의 task 등가물.
  const [sort, setSort] = useViewMode<SortKey>('project-board.backlog.sort', 'date-desc');

  const visible = tasks.filter((task) => !hideDone || task.status !== 'done');
  // 기본 순서는 상태 버킷 → 에픽 → task id다. 정렬을 고르면 그 대신 갱신 시각으로 줄을 세운다 —
  // task에는 등록 시점이 없다(`sorting.ts` 표 참고).
  const rows =
    sort === 'default'
      ? [...visible].sort(
          (left, right) =>
            BUCKETS.findIndex((bucket) => bucket.key === left.status) -
              BUCKETS.findIndex((bucket) => bucket.key === right.status) ||
            left.epicSlug.localeCompare(right.epicSlug) ||
            left.taskId.localeCompare(right.taskId),
        )
      : DateSort.sort(visible, sort, (task) => task.updatedAt, (task) => task.uid);

  const open = (task: Task, setPayload: (payload: DrawerPayload) => void): void =>
    setPayload(DrawerPayloads.task(task, EpicMembership.epicOf(index?.epics ?? [], task), index?.tasks ?? []));

  return (
    <ViewFrame
      scope="tasks"
      countLabel={`${rows.length}개 task`}
      isEmpty={rows.length === 0}
      emptyMessage={hideDone ? '남은 task가 없다 — 완료 숨김을 끄면 끝난 일도 보인다.' : '조건에 맞는 task가 없다.'}
      toolbar={
        <>
          <label className="flex items-center gap-1.5 whitespace-nowrap text-muted">
            <input type="checkbox" checked={hideDone} onChange={(event) => setHideDone(event.target.checked)} />
            완료 숨김
          </label>
          <SortPicker options={SORT_OPTIONS} value={sort} onChange={setSort} label="task 정렬" />
          <ViewModeToggle options={VIEW_MODES} value={mode} onChange={setMode} />
        </>
      }
      render={(setPayload) =>
        mode === 'graph' ? (
          <BacklogGraphs tasks={rows} index={index} hideDone={hideDone} setPayload={setPayload} />
        ) : mode === 'card' ? (
          <BacklogCards tasks={rows} index={index} open={(task) => open(task, setPayload)} />
        ) : (
          <BacklogTable tasks={rows} index={index} open={(task) => open(task, setPayload)} />
        )
      }
    />
  );
}

/**
 * 백로그는 읽는 화면이라 제목을 자르지 않고 줄바꿈한다(열 폭은 고정).
 *
 * "담당" 열은 문서 셀(`task.owner`) 그대로가 아니라 필터와 **같은 판정**(`assigneeOf`)을 쓴다 — 필터가
 * 참고하는 이름과 화면에 보이는 이름이 다르면, 담당자로 좁혀도 사람이 보기엔 안 걸러진 것처럼 보인다.
 */
function BacklogTable({
  tasks,
  index,
  open,
}: {
  tasks: Task[];
  index: BoardIndex | null;
  open: (task: Task) => void;
}): React.JSX.Element {
  return (
    <div className="rounded-lg border border-line bg-surface">
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col className="w-[58px]" />
          <col />
          <col className="w-[190px]" />
          <col className="w-[140px]" />
          <col className="w-[86px]" />
          <col className="w-[70px]" />
          <col className="w-[120px]" />
          <col className="w-[96px]" />
        </colgroup>
        <thead>
          <tr className={HEAD}>
            <th>ID</th>
            <th>제목</th>
            <th>에픽</th>
            <th>Repo</th>
            <th>담당</th>
            <th>예상</th>
            <th>상태</th>
            <th>갱신</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => (
            <tr
              key={task.uid}
              title={task.title}
              onClick={() => open(task)}
              className="cursor-pointer border-b border-line-soft last:border-0 hover:bg-canvas [&>td]:px-2.5 [&>td]:py-2.5 [&>td]:align-top"
            >
              <td className="font-mono text-[11px] whitespace-nowrap text-muted">{task.taskId}</td>
              <td>
                <div className={`flex flex-wrap items-center gap-1.5 leading-relaxed ${task.status === 'done' ? 'text-muted' : ''}`}>
                  {task.titleTag && (
                    <span className="rounded bg-line-soft px-1.5 text-[11px] font-semibold text-muted">
                      {task.titleTag}
                    </span>
                  )}
                  {task.titleHead}
                  {task.blockedBy.length > 0 && <Chip tone="warn">⛔ {task.blockedBy.join(',')}</Chip>}
                </div>
                {task.titleDetail && <div className="mt-0.5 text-xs leading-relaxed text-muted">{task.titleDetail}</div>}
              </td>
              <td className="truncate text-[11px] text-muted">{task.epicSlug}</td>
              <td className="whitespace-nowrap">{task.repo && <Chip tone="repo">{task.repo}</Chip>}</td>
              <td className="whitespace-nowrap">
                {OwnerRoster.assigneeOf(task, EpicMembership.epicOf(index?.epics ?? [], task)) || '-'}
              </td>
              <td className="whitespace-nowrap tabular-nums">{task.estimate || '-'}</td>
              <td className="whitespace-nowrap">
                <StatusDot status={task.status} />
                {task.statusToken || '-'}
              </td>
              <td className="text-[11px] whitespace-nowrap text-muted">{task.updatedAt}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * 카드 뷰 — 테이블이 훑는 화면이라면 이건 **한 건씩 읽는 화면**이다. 열 폭에 눌려 잘리던 제목·진행 메모를
 * 펴서 보여주고, 에픽으로 묶어 "어느 에픽 일이 몰려 있나"가 눈에 들어오게 한다.
 */
function BacklogCards({
  tasks,
  index,
  open,
}: {
  tasks: Task[];
  index: BoardIndex | null;
  open: (task: Task) => void;
}): React.JSX.Element {
  const titleOf = (slug: string): string => index?.epics.find((epic) => epic.slug === slug)?.title ?? slug;
  const assignee = (task: Task): string => OwnerRoster.assigneeOf(task, EpicMembership.epicOf(index?.epics ?? [], task));

  return (
    <div className="flex flex-col gap-7">
      {groupByEpic(tasks).map((group) => (
        <section key={group.slug}>
          <h2 className="mb-3 flex items-center gap-2.5 text-[13px] font-bold">
            {titleOf(group.slug)}
            <span className="rounded-full bg-line-soft px-1.5 text-[11px] font-semibold text-muted">
              {group.tasks.length}
            </span>
            <span className="h-px flex-1 bg-line" />
          </h2>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(330px,1fr))] gap-3">
            {group.tasks.map((task) => (
              <article
                key={task.uid}
                title={task.title}
                onClick={() => open(task)}
                className={`cursor-pointer rounded-lg border border-line border-l-[3px] bg-surface p-3.5 transition hover:border-accent hover:shadow-sm ${EDGE[task.status] ?? EDGE.unknown}`}
              >
                <header className="mb-1.5 flex items-center gap-1.5">
                  <span className="font-mono text-[11px] text-muted">{task.taskId}</span>
                  {task.titleTag && (
                    <span className="rounded bg-line-soft px-1.5 text-[11px] font-semibold text-muted">
                      {task.titleTag}
                    </span>
                  )}
                  <span className="flex-1" />
                  <span className="text-[11.5px] whitespace-nowrap text-muted">
                    <StatusDot status={task.status} />
                    {task.statusToken || '-'}
                  </span>
                </header>

                <div className={`text-[13.5px] leading-snug font-semibold ${task.status === 'done' ? 'text-muted' : ''}`}>
                  {task.titleHead}
                </div>
                {task.titleDetail && (
                  <p className="mt-1 line-clamp-3 text-[11.5px] leading-relaxed text-muted">{task.titleDetail}</p>
                )}

                <footer className="mt-2.5 flex flex-wrap items-center gap-1.5">
                  {task.repo && <Chip tone="repo">{task.repo}</Chip>}
                  {assignee(task) && <Chip>{assignee(task)}</Chip>}
                  {task.estimate && <Chip>{task.estimate}</Chip>}
                  {task.blockedBy.length > 0 && <Chip tone="warn">⛔ {task.blockedBy.join(',')}</Chip>}
                  <span className="flex-1" />
                  <span className="text-[11px] text-faint">{task.updatedAt || '-'}</span>
                </footer>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * 그래프 뷰 — **에픽마다 따로** 그린다.
 *
 * 선행(`Depends On`)은 에픽 안에서만 유일한 task 번호라, 전 에픽을 한 그래프에 합치면 다른 에픽의 같은
 * 번호끼리 이어져 없는 관계가 생긴다. 그래서 에픽 경계를 넘지 않는다.
 *
 * 선행 관계가 하나도 없는 에픽은 그리지 않는다 — 선 없는 노드만 늘어놓으면 관계가 있는 에픽이 묻힌다.
 * 대신 몇 개를 안 그렸는지 아래에 밝힌다.
 */
function BacklogGraphs({
  tasks,
  index,
  hideDone,
  setPayload,
}: {
  tasks: Task[];
  index: BoardIndex | null;
  hideDone: boolean;
  setPayload: (payload: DrawerPayload) => void;
}): React.JSX.Element {
  const all = index?.tasks ?? [];
  const groups = groupByEpic(tasks).map((group) => ({
    ...group,
    epic: index?.epics.find((epic) => epic.slug === group.slug),
    children: childrenOf(group.tasks, all, index),
  }));

  const linked = groups.filter((group) => hasEdge(group.children));
  const isolated = groups.length - linked.length;

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-1">
        <p className="text-xs text-faint">
          선행 관계는 에픽 안에서만 이어진다 — 에픽별로 따로 그린다.
          {hideDone && ' 완료를 숨긴 상태라 끝난 선행은 그래프에서 빠진다 — 1단은 지금 착수할 수 있는 task다.'}
        </p>
        <DependencyLegend />
      </div>

      {linked.map((group) => (
        <section key={group.slug}>
          <h2 className="mb-3 flex items-center gap-2.5 text-[13px] font-bold">
            {group.epic?.title ?? group.slug}
            <span className="rounded-full bg-line-soft px-1.5 text-[11px] font-semibold text-muted">
              {group.children.length}
            </span>
            <span className="h-px flex-1 bg-line" />
          </h2>
          <DependencyGraph items={group.children} onOpen={(child) => setPayload(child.open())} legend={false} />
        </section>
      ))}

      {linked.length === 0 && (
        <p className="text-sm text-muted">보이는 task 중 선행 관계가 있는 것이 없다.</p>
      )}
      {isolated > 0 && (
        <p className="text-xs text-faint">
          선행 관계가 없는 에픽 {isolated}개는 그리지 않았다 — 테이블·카드 뷰에서 확인한다.
        </p>
      )}
    </div>
  );
}

/** 그래프가 읽는 형태로. 상세는 에픽의 전체 task를 형제로 넘겨 열어야 왕복 이동이 끊기지 않는다. */
function childrenOf(tasks: Task[], all: Task[], index: BoardIndex | null): DrawerChild[] {
  return [...tasks]
    .sort((left, right) => left.taskId.localeCompare(right.taskId, undefined, { numeric: true }))
    .map((task) => ({
      key: task.taskId,
      label: task.titleHead || task.title || '(제목 없음)',
      status: task.status,
      statusToken: task.statusToken,
      hint: [OwnerRoster.assigneeOf(task, EpicMembership.epicOf(index?.epics ?? [], task)), task.repo, task.estimate]
        .filter(Boolean)
        .join(' · '),
      dependsOn: task.dependsOn,
      blockedBy: task.blockedBy,
      open: () => DrawerPayloads.task(task, EpicMembership.epicOf(index?.epics ?? [], task), all),
    }));
}

/** 지금 보이는 task 사이에 실제로 이어지는 선행이 있는가. 밖을 가리키는 선행은 그려지지 않는다. */
function hasEdge(children: DrawerChild[]): boolean {
  const keys = new Set(children.map((child) => child.key));
  return children.some((child) => child.dependsOn.some((id) => keys.has(id)));
}

/** 정렬(상태→에픽→ID)을 유지한 채 에픽으로 묶는다 — 묶음 안에서도 할 일이 먼저 온다. */
function groupByEpic(tasks: Task[]): Array<{ slug: string; tasks: Task[] }> {
  const groups: Array<{ slug: string; tasks: Task[] }> = [];
  for (const task of [...tasks].sort((left, right) => left.epicSlug.localeCompare(right.epicSlug))) {
    const group = groups.find((candidate) => candidate.slug === task.epicSlug);
    if (group) group.tasks.push(task);
    else groups.push({ slug: task.epicSlug, tasks: [task] });
  }
  return groups;
}
